import { useState } from 'react'
import clsx from 'clsx'
import { ChipInput } from '@/components/ChipInput'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Select } from '@/components/Select'
import type { LookupTypeOption } from '@/lib/lookupTypes'
import type { LookupEntry } from '@/types/bundle'
import { isValidDomainShape } from '../lookupUtils'

const NEW_TYPE_SENTINEL = '__new_type__'

export interface LookupEntryFormProps {
  entry: LookupEntry
  onChange: (entry: LookupEntry) => void
  /** Every distinct type currently in use across the table, plus the two
   * legacy defaults - never a fixed, closed list (src/lib/lookupTypes.ts). */
  typeOptions: LookupTypeOption[]
  serverError?: string
  isNew?: boolean
}

// The expanded accordion body - Name/Type/Aliases/Domain edit form, rendered
// via DataTable's `renderExpanded` directly below the collapsed row.
export function LookupEntryForm({ entry, onChange, typeOptions, serverError, isNew }: LookupEntryFormProps) {
  const domain = entry.domain ?? ''
  const domainInvalid = domain.trim().length > 0 && !isValidDomainShape(domain.trim())
  // Local UI mode only - `entry.type` itself never becomes the sentinel
  // value, so a stray unsaved sentinel can never reach the backend.
  const [isAddingNewType, setIsAddingNewType] = useState(false)

  return (
    <div
      className={clsx(
        'flex flex-col gap-4 bg-surface p-4',
        isNew && 'animate-[field-row-enter_200ms_ease-out]',
      )}
    >
      <div className="flex flex-col gap-1">
        <label className="text-meta font-medium text-text-secondary" htmlFor="lookup-name">
          Name
        </label>
        <input
          id="lookup-name"
          type="text"
          value={entry.name}
          onChange={(event) => onChange({ ...entry, name: event.target.value })}
          className="w-96 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none"
        />
        {entry.name.trim().length === 0 && <span className="text-meta text-danger">Name is required</span>}
        {serverError && <span className="text-meta text-danger">{serverError}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="lookup-type">
          Type
          <InfoTooltip text="Estate Agent, Solicitor Firm, or any other category you've added. Decides which category this entry appears under and how it's matched." />
        </label>
        {isAddingNewType ? (
          <div className="flex items-center gap-2">
            <input
              id="lookup-type"
              type="text"
              autoFocus
              value={entry.type}
              onChange={(event) => onChange({ ...entry, type: event.target.value })}
              placeholder="e.g. Letting Agent"
              className="w-48 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
            />
            <button
              type="button"
              onClick={() => setIsAddingNewType(false)}
              className="text-meta font-medium text-accent hover:underline"
            >
              Choose existing instead
            </button>
          </div>
        ) : (
          <Select
            id="lookup-type"
            value={entry.type}
            onChange={(value) => {
              if (value === NEW_TYPE_SENTINEL) {
                setIsAddingNewType(true)
                onChange({ ...entry, type: '' })
                return
              }
              onChange({ ...entry, type: value })
            }}
            options={[...typeOptions, { value: NEW_TYPE_SENTINEL, label: '+ Add new type…' }]}
            className="w-48"
          />
        )}
        {isAddingNewType && entry.type.trim().length === 0 && (
          <span className="text-meta text-danger">Type is required</span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
          Aliases
          <InfoTooltip text="Other names this entry might appear as in documents. Matches must be exact, not similar." />
        </span>
        <ChipInput
          value={entry.aliases ?? []}
          onChange={(aliases) => onChange({ ...entry, aliases })}
          placeholder="Type an alias, press Enter…"
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="lookup-domain">
          Domain
          <InfoTooltip text="The email domain linked to this entry, like example.co.uk. Helps match documents to the right firm." />
        </label>
        <input
          id="lookup-domain"
          type="text"
          value={domain}
          placeholder="example.co.uk"
          onChange={(event) => onChange({ ...entry, domain: event.target.value })}
          className={clsx(
            'w-64 rounded-control border bg-bg px-3 py-2 text-body text-text-primary outline-none',
            domainInvalid ? 'border-danger' : 'border-border',
          )}
        />
        {domainInvalid && <span className="text-meta text-danger">Doesn't look like a valid domain</span>}
      </div>
    </div>
  )
}
